import { useRef, useState, forwardRef } from "react";

import { useTheme } from "@mui/material/styles";
import MuiInput from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";


import coll from "core/collections";

import numbers from "core/numbers";
import validation from "core/validation";
import useInterval from "core/hooks/useInterval";
import useToggle from "core/hooks/useToggle";

import Box from "ui/atom/Box";


const i = { timer: 30, interval: 1000 };

const Input = forwardRef(({ value, label, rootSx, info, errors, onChange, maxLength, helper = true, type = "text", asterisk = true, ...props }, ref) => {
	const theme = useTheme();
	const [focus, toggleFocus] = useToggle(false);

	const style = {
		root: { width: "100%", ...rootSx },
		input: {
			p: theme.spacing(1.25, 2),
			backgroundColor: "#fff",
			borderRadius: "12px",
			border: "2px solid",
			borderColor: errors?.length ? theme.palette.error.main : focus ? theme.palette.border.focus : theme.palette.border.input,
			"& .MuiInputBase-input": { p: 0 },
			"& input[type=number]": { "MozAppearance": "textfield" },
			"& input[type=number]::-webkit-outer-spin-button": {
				"WebkitAppearance": "none",
				margin: 0
			},
			"& input[type=number]::-webkit-inner-spin-button": {
				"WebkitAppearance": "none",
				margin: 0
			}
		},
		text: { pl: 1.25 }
	};

	const change = (e) => {
		let val = e.target.value;
		if (type === "tel") {
			val = numbers.toPhone(val);
			e.target.value = val;
		}
		if (onChange) onChange(e);
	};

	return (
		<Box.Column gap={0.75} sx={style.root}>
			{label && <Typography variant="h6" fontSize={14} color="text.label" sx={{ alignSelf: "flex-start" }}>{label}{props.required && asterisk && "*"}</Typography>}
			<Box.Column gap={0.5}>
				<MuiInput
					fullWidth
					value={value}
					onBlur={toggleFocus}
					onFocus={toggleFocus}
					onChange={change}
					type={type}
					inputRef={ref}
					{...props}
					slotProps={{
						input: { maxLength }
					}}
					sx={{ ...style.input, ...props.sx }}
				/>
			</Box.Column>
			{(maxLength && helper) && <Typography variant="subtitle2" colort="text.secondary" sx={style.text}>{validation.remaining(value, maxLength)}</Typography>}
			{errors && (
				<Box.Column gap={0.5} sx={style.text}>
					{errors.map((e, k) => <Typography variant="body3" color="error" key={k}>{e}</Typography>)}
				</Box.Column>
			)}
			{info && (
				<Typography variant="body3" sx={{ px: 2 }}>
					{info}
				</Typography>
			)}
		</Box.Column>
	);
});

Input.Otp = ({ value, setValue, destination, length = 6, onSubmit, resend, disabled }) => {
	const theme = useTheme();
	const [timer, setTimer] = useState(i.timer);
	const [interval, setInterval] = useState(i.interval);
	const ref = useRef(Array(length).fill(null));

	const style = {
		root: { alignItems: "center", mt: 1, gap: theme.spacing(2) },
		stopwatch: { color: "tertiary.main", alignItems: "center" },
		input: { p: 0, width: "44px", height: "44px" },
		text: { alignItems: "center" },
		resend: {
			cursor: timer ? "default" : "pointer",
			transition: "filter 200ms ease",
			":hover": !timer && { filter: "brightness(0.9)" }
		},
		button: { maxWidth: "300px" }
	};

	const change = (input, index) => {
		const arr = value.split("");
		arr[index] = input[input.length - 1] || "";
		setValue(arr.join(""));

		if (input.length === 1 && index < length - 1) ref.current[index + 1]?.focus();
		else if (input.length === 0 && index > 0) ref.current[index - 1]?.focus();

		if (arr.length === length && onSubmit) onSubmit(arr.join(""));
	};

	const pressed = (e, index) => {
		if (e.key === "ArrowLeft" && index > 0) ref.current[index - 1]?.focus(); /* move focus to left */
		else if (e.key === "ArrowRight" && index < length - 1) ref.current[index + 1]?.focus(); /* move focus to right */
	};

	const reset = async () => {
		if (timer === 0) {
			resend && await resend();
			setInterval(1000);
			setTimer(i.timer);
			setValue("");
			ref.current[0]?.focus();
		}
	};

	const paste = (e, index) => {
		e.preventDefault();
		const arr = [...value];

		const v = e.clipboardData.getData("text").trim().slice(0, length);
		for (let i = 0; i < v.length; i++) {
			arr[i] = v[i];
			setValue(arr.join(""));
		}

		const next = ref.current[Math.min(index + v.length, length - 1)];
		if (arr.length === length && onSubmit) onSubmit(arr.join(""));
		else if (next) next.focus();
	};

	useInterval(() => {
		if (interval > 0) {
			setTimer((t) => {
				if (t === 0) {
					setInterval(0);
					return t;
				}
				return t - 1;
			});
		}
	}, interval);

	return (
		<Box.Column sx={style.root}>
			<Box.Column gap={0.5}>
				<Typography variant="h6" color="text.primary" align="center">Your sign-in code was sent to </Typography>
				<Typography variant="h6" align="center" color="link.main">{destination}</Typography>
			</Box.Column>
			<Box gap={1}>
				{coll.array.create(length).map((_, i) => (
					<Input
						value={value[i] || ""}
						maxLength={1}
						helper={false}
						onChange={e => change(e.target.value, i)}
						onKeyDown={e => pressed(e, i)}
						onPaste={e => paste(e, i)}
						sx={style.input}
						inputProps={{ sx: { textAlign: "center" } }}
						required
						autoFocus={i === 0}
						type="number"
						disabled={disabled}
						ref={(r) => (ref.current[i] = r)}
						key={i} />
				))}
			</Box>
			<Box gap={0.5} sx={style.text}>
				<Typography variant="body2" color="text.primary">Didn’t get your code?</Typography>
				<Typography component="span" color={timer ? "text.disabled" : "tertiary.main"} onClick={reset} sx={style.resend}>
					Resend {timer > 0 && <Typography component="span">in 00:{numbers.pad(timer)}</Typography>}
				</Typography>
			</Box>
		</Box.Column>
	);
};

Input.Phone = ({ country, value, label = "Phone", onChange, info, ...props }) => {
	const [message, setMessage] = useState(false);
	const error = message && ["Right now, we only support US phone numbers. Please sign up with your email address instead."];
	const style = {
		root: { width: "100%" },
		inputs: {
			width: "100%",
			alignItems: "flex-start"
		},
		country: {
			width: "70px"
		},
		countryInput: {
			borderBottomRightRadius: 0,
			borderTopRightRadius: 0
		},
		input: {
			borderBottomLeftRadius: 0,
			borderTopLeftRadius: 0
		}
	};

	return (
		<Box.Column gap={0.75} sx={style.inputs}>
			{label && <Typography variant="body3" color="text.primary" sx={{ alignSelf: "flex-start" }}>{label}{props.required && props.asterisk && "*"}</Typography>}
			<Box sx={style.inputs}>
				<Input value={country} rootSx={style.country} sx={style.countryInput} onChange={() => setMessage(true)} disabled={props.disabled} fullWidth={false} />
				<Input name="phone" type="tel" placeholder="(###) ###-####" value={value} onChange={onChange} autoComplete="on" onFocus={() => setMessage(false)} autoFocus {...props} errors={error || props.errors} sx={{ ...style.input, ...props.sx }} />
			</Box>
			{info && (
				<Typography variant="body3" sx={{ px: 2 }}>
					{info}
				</Typography>
			)}
		</Box.Column>
	);
};

Input.Email = (props) => <Input label="Email" name="email" type="email" placeholder="name@email.com" autoComplete="on" autoFocus {...props} />;

Input.Select = ({ label, options, rootSx, errors, ...props }) => {
	const theme = useTheme();
	const [focus, setFocus] = useState(false);

	const style = {
		root: { width: "100%", ...rootSx },
		select: {
			backgroundColor: theme.palette.background.paper,
			borderRadius: theme.spacing(2),
			border: "2px solid",
			borderColor: errors?.length ? theme.palette.error.main : focus ? theme.palette.border.focus : theme.palette.border.input,
			".MuiSelect-select": {
				p: theme.spacing(1.25, 2)
			},
			"&:hover, &.Mui-focused": {
				backgroundColor: theme.palette.background.paper
			}
		}
	};

	const toggle = () => setFocus(s => !s);

	return (
		<Box.Column gap={0.75} sx={style.root}>
			<Typography variant="body3" color="text.primary" sx={{ alignSelf: "flex-start" }}>{label}{props.required && label && "*"}</Typography>
			<Select variant="filled" sx={style.select} disableUnderline onFocus={toggle} onBlur={toggle} {...props}>
				{options.map((o, k) => <MenuItem value={o.value} key={k}>{o.label}</MenuItem>)}
			</Select>
		</Box.Column>
	);
};

export default Input;